import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { connectSocket, getSocket } from "../core/socket";
import { isTokenPresent } from "../core/auth";
import { isLocallyRecentlyInvalidated } from "../core/mutationInvalidator";
import { emitDomainChanged } from "../core/domainEvents";
import { invalidateAuthValidateCache } from "../core/authValidate";
import { refreshUserToken } from "../utils/roleHelper";
import { queryKeys } from "./queryKeys";

/**
 * Hook globale (montato una volta in App): apre il socket e traduce gli
 * eventi del backend in invalidazioni di React Query, come su gestionale-gls.
 *
 * A ogni connessione - anche la prima - si rileggono le query visibili: la
 * cache persistita può essere vecchia di ore.
 *
 * Le presenze arrivano anche dal link pubblico dei clienti: lo studio vede
 * le giornate comparire mentre il cliente le segna, senza ricaricare.
 */
export function useRealtimeSync() {
  const qc = useQueryClient();
  const connectedOnce = useRef(false);
  const [session, setSession] = useState(0);

  useEffect(() => {
    const on = () => setSession((n) => n + 1);
    window.addEventListener("pr:session", on);
    return () => window.removeEventListener("pr:session", on);
  }, []);

  useEffect(() => {
    if (!isTokenPresent()) return undefined;
    const socket = connectSocket();
    if (!socket) return undefined;

    const onConnect = () => {
      if (connectedOnce.current) invalidateAuthValidateCache();
      connectedOnce.current = true;
      qc.invalidateQueries({ refetchType: "active" });
    };

    const inv = (domain, ...keys) => {
      if (domain && isLocallyRecentlyInvalidated(domain)) return;
      keys.forEach((key) => qc.invalidateQueries({ queryKey: key, refetchType: "active" }));
      if (domain) emitDomainChanged(domain);
    };

    const handlers = {
      connect: onConnect,
      "clienti:changed": () => inv("clienti", ["clienti"], queryKeys.clientiElenco, ["cliente"], queryKeys.panoramica, queryKeys.piano),
      "dipendenti:changed": () =>
        inv("dipendenti", ["dipendenti"], ["dipendente"], ["dipendentePresenze"], ["presenzeMese"], ["clienti"], ["scadenze"], queryKeys.panoramica, queryKeys.piano),
      "presenze:changed": (p) => {
        // Un evento del cliente non è mai "locale": si rilegge sempre.
        const daCliente = p && p.fonte === "cliente";
        if (!daCliente && isLocallyRecentlyInvalidated("presenze")) return;
        const mese = p && p.clienteId && p.anno ? queryKeys.presenzeMese(p.clienteId, p.anno, p.mese) : ["presenzeMese"];
        [mese, ["clienti"], queryKeys.panoramica, ["dipendentePresenze"], ["dipendente"]].forEach((key) =>
          qc.invalidateQueries({ queryKey: key, refetchType: "active" }),
        );
        emitDomainChanged("presenze");
        window.dispatchEvent(new CustomEvent("pr:presenze", { detail: p || {} }));
      },
      "scadenze:changed": () => inv("scadenze", ["scadenze"], ["scadenza"], ["dipendente"], queryKeys.panoramica),
      "ocr:changed": (p) => {
        const keys = [queryKeys.ocrLotti, queryKeys.ocrStato, queryKeys.panoramica];
        if (p && p.lottoId) keys.push(queryKeys.ocrLotto(p.lottoId));
        else keys.push(["ocrLotto"]);
        keys.forEach((key) => qc.invalidateQueries({ queryKey: key, refetchType: "active" }));
        window.dispatchEvent(new CustomEvent("pr:ocr", { detail: p || {} }));
      },
      "utenti:changed": () => {
        invalidateAuthValidateCache();
        inv("utenti", queryKeys.users, queryKeys.people, queryKeys.ruoli, queryKeys.inviti, queryKeys.richieste, ["clienti"], queryKeys.clientiElenco);
      },
      "azienda:changed": () => inv("azienda", queryKeys.azienda, queryKeys.impostazioni, ["impostazioniCliente"], ["impostazioniDipendente"]),
      "paghe:changed": () => inv("paghe", ["paghe"], ["pagheCliente"], ["cedolino"], queryKeys.pagheTabelle, ["pagheCatalogo"], queryKeys.panoramica),
      "timbrature:changed": () => inv("timbrature", ["timbrature"], ["presenzeMese"], ["impostazioniCliente"]),
      "importazioni:changed": () => inv("importazioni", queryKeys.importazioni, queryKeys.importProfili),
      "abbonamento:changed": () => inv("abbonamento", queryKeys.subscription, queryKeys.piano, queryKeys.panoramica, queryKeys.contratto),
      "notifiche:new": (n) => {
        qc.setQueryData(queryKeys.notifiche, (old) => (old ? { ...old, notifiche: [n, ...old.notifiche].slice(0, 60), nonLette: (old.nonLette || 0) + 1 } : old));
        qc.invalidateQueries({ queryKey: queryKeys.notifiche, refetchType: "none" });
        window.dispatchEvent(new CustomEvent("pr:notifica", { detail: n }));
      },
      // Cambio di ruoli o permessi DI QUESTO utente: si rinnova il token e
      // si rilegge tutto, così menu e pagine si aggiornano senza login.
      "permissions:changed": async () => {
        invalidateAuthValidateCache();
        try {
          await refreshUserToken();
        } catch {
          /* l'interceptor gestirà il prossimo 401 */
        }
        qc.invalidateQueries({ refetchType: "active" });
        window.dispatchEvent(new CustomEvent("pr:permissions-changed"));
      },
    };
    // I contatori del menu dipendono da quasi tutto: si rileggono a ogni evento di dominio.
    const contatori = (event) => {
      if (/:changed$/.test(event)) qc.invalidateQueries({ queryKey: queryKeys.contatori, refetchType: "active" });
    };
    Object.entries(handlers).forEach(([ev, fn]) => socket.on(ev, fn));
    socket.onAny(contatori);
    if (socket.connected) onConnect();

    return () => {
      const s = getSocket();
      if (!s) return;
      Object.entries(handlers).forEach(([ev, fn]) => s.off(ev, fn));
      s.offAny(contatori);
    };
  }, [qc, session]);
}
